import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import Logo from "@/components/Logo";
import Footer from "@/components/Footer";
import PromoBanner from "@/components/PromoBanner";
import { useAuth } from "@/context/AuthContext";
import { PAGES, pageStructuredData } from "@/lib/marketingContent";
import { ArrowRight, Check } from "lucide-react";

/**
 * Mutates <head>: title, meta description, canonical, JSON-LD blocks.
 * Cleans up its own JSON-LD nodes on unmount so we never leak schema between routes.
 */
const useSeoHead = (slug) => {
  const page = PAGES[slug];
  useEffect(() => {
    if (!page) return undefined;
    const prevTitle = document.title;
    document.title = page.title;

    const setMeta = (name, content) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    setMeta("description", page.description);

    let canonical = document.getElementById("rt-canonical")
      || document.querySelector('link[rel="canonical"]');
    const prevHref = canonical?.getAttribute("href");
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      canonical.setAttribute("id", "rt-canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", `https://retainertracker.com/${slug}`);

    // Keep og:url in sync (some social/AI crawlers honour it more than canonical).
    const ogUrl = document.getElementById("rt-og-url");
    const prevOg = ogUrl?.getAttribute("content");
    if (ogUrl) ogUrl.setAttribute("content", `https://retainertracker.com/${slug}`);

    // JSON-LD blocks tagged so we can remove them on unmount.
    const tag = "data-rt-marketing-schema";
    const added = [];
    pageStructuredData(slug).forEach((schema) => {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.setAttribute(tag, "1");
      script.text = JSON.stringify(schema);
      document.head.appendChild(script);
      added.push(script);
    });

    return () => {
      document.title = prevTitle;
      if (canonical && prevHref) canonical.setAttribute("href", prevHref);
      if (ogUrl && prevOg) ogUrl.setAttribute("content", prevOg);
      added.forEach((n) => n.parentNode && n.parentNode.removeChild(n));
    };
  }, [slug, page]);
};

const Section = ({ section }) => (
  <section className="mt-10">
    <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight text-[#1F2326] mb-4">
      {section.h2}
    </h2>
    {section.body ? <p className="text-base leading-relaxed text-[#1F2326]">{section.body}</p> : null}
    {section.paragraphs?.map((p, i) => (
      <p key={i} className="text-base leading-relaxed text-[#1F2326] mt-3">
        {p}
      </p>
    ))}
    {section.list?.length ? (
      <ul className="mt-4 space-y-2">
        {section.list.map((item, i) => (
          <li key={i} className="flex items-start gap-2.5 text-base text-[#1F2326]">
            <Check className="w-4 h-4 mt-1 text-[#B89B5E] flex-none" strokeWidth={2.5} />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    ) : null}
    {section.formula ? (
      <div className="mt-4 inline-block bg-[#1F2326] text-[#F8F6F1] rounded-lg px-4 py-3 font-mono text-sm">
        {section.formula}
      </div>
    ) : null}
    {section.table ? (
      <div className="mt-5 overflow-x-auto">
        <table className="min-w-full text-sm border border-[#E4DFD2] rounded-lg overflow-hidden">
          <thead>
            <tr className="bg-[#FBFAF6]">
              {section.table.head.map((h, i) => (
                <th key={i} className="text-left font-semibold px-4 py-2.5 text-[#1F2326]">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {section.table.rows.map((row, ri) => (
              <tr key={ri} className="border-t border-[#EDE7D6]">
                {row.map((cell, ci) => (
                  <td key={ci} className="px-4 py-2.5 text-[#1F2326]">
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    ) : null}
    {section.outro ? (
      <p className="text-base leading-relaxed text-[#1F2326] mt-4">{section.outro}</p>
    ) : null}
  </section>
);

const ConversionBlock = ({ onCta, ctaTestId }) => (
  <section
    className="mt-14 bg-[#1F2326] text-[#F8F6F1] rounded-2xl p-8 sm:p-10"
    data-testid="page-conversion-cta"
  >
    <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight">
      Ready to check your own retainers?
    </h2>
    <p className="text-[#C9C2B0] mt-3 max-w-xl">
      Track included hours, logged time, delivery cost, profit, and status in one simple dashboard.
    </p>
    <div className="mt-5 flex flex-wrap items-center gap-2">
      <button
        type="button"
        onClick={onCta}
        data-testid={ctaTestId}
        className="inline-flex items-center gap-1.5 bg-[#B89B5E] text-[#1F2326] hover:bg-[#A78946] px-5 py-2.5 rounded-lg font-semibold text-sm transition-colors"
      >
        Start free trial
        <ArrowRight className="w-4 h-4" />
      </button>
      <Link
        to="/pricing"
        data-testid="page-view-pricing-link"
        className="text-sm text-[#C9C2B0] hover:text-white px-3 py-2.5"
      >
        View pricing
      </Link>
    </div>
    <div className="mt-3 text-xs text-[#C9C2B0]">
      14-day free trial. No credit card needed.
    </div>
  </section>
);

const MarketingPage = ({ slug }) => {
  const page = PAGES[slug];
  const { user } = useAuth();
  const navigate = useNavigate();
  useSeoHead(slug);

  if (!page) return null;

  const handleCta = () => {
    if (user) navigate(page.cta.url);
    else {
      const redirect = window.location.origin + page.cta.url;
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F6F1] text-[#1F2326] flex flex-col" data-testid={`marketing-${slug}`}>
      <PromoBanner />
      <header className="sticky top-0 z-30 backdrop-blur-md bg-[#F8F6F1]/85 border-b border-[#E4DFD2]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4 flex items-center justify-between">
          <Link to="/" data-testid="marketing-logo">
            <Logo size={28} showTagline />
          </Link>
          <nav className="flex items-center gap-2">
            <Link to="/pricing" className="text-sm text-[#1F2326] hover:text-[#B89B5E] px-3 py-2">
              Pricing
            </Link>
            <Link to="/faqs" className="text-sm text-[#1F2326] hover:text-[#B89B5E] px-3 py-2 hidden sm:inline">
              FAQs
            </Link>
            <button
              type="button"
              onClick={handleCta}
              data-testid="marketing-nav-cta"
              className="bg-[#1F2326] text-[#F8F6F1] hover:bg-[#2A2E32] px-4 py-2 rounded-lg text-sm font-medium"
            >
              Start free trial
            </button>
          </nav>
        </div>
      </header>

      <main className="flex-1">
        <div className="max-w-3xl mx-auto px-6 py-14 lg:py-20">
          {/* Breadcrumb */}
          <nav className="text-xs text-[#69747C] mb-4" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-[#1F2326]">Home</Link>
            <span className="mx-2">/</span>
            <span className="text-[#1F2326]">{page.h1}</span>
          </nav>

          <div className="text-[11px] tracking-[0.1em] uppercase font-semibold text-[#B89B5E]">
            Retainer Tracker
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tighter text-[#1F2326] mt-2">
            {page.h1}
          </h1>

          {page.intro?.map((p, i) => (
            <p
              key={i}
              className={`leading-relaxed text-[#1F2326] ${i === 0 ? "mt-6 text-lg" : "mt-3 text-base"}`}
            >
              {p}
            </p>
          ))}

          {page.contextualLinks?.length ? (
            <p
              className="mt-4 text-base leading-relaxed text-[#1F2326]"
              data-testid="contextual-links-paragraph"
            >
              {page.contextualLinks.map((link, i) => (
                <span key={link.to}>
                  {i === 0 ? "See also: " : i === page.contextualLinks.length - 1 ? " and " : ", "}
                  <Link
                    to={link.to}
                    className="text-[#1F2326] underline decoration-[#B89B5E] decoration-2 underline-offset-4 hover:decoration-[#1F2326]"
                    data-testid={`contextual-link-${link.to.replace(/\W+/g, "-")}`}
                  >
                    {link.label}
                  </Link>
                  {i === page.contextualLinks.length - 1 ? "." : ""}
                </span>
              ))}
            </p>
          ) : null}

          {page.screenshot ? (
            <figure
              data-testid="page-screenshot"
              className="mt-8 bg-white border border-[#E4DFD2] rounded-2xl overflow-hidden"
            >
              <img
                src={page.screenshot.src}
                alt={page.screenshot.alt}
                loading="lazy"
                width="1120"
                height="640"
                className="w-full h-auto block bg-[#FBFAF6]"
                onError={(e) => {
                  // If the screenshot PNG isn't uploaded yet, hide the figure instead of showing a broken image.
                  e.currentTarget.parentElement.style.display = "none";
                }}
              />
              {page.screenshot.caption ? (
                <figcaption className="px-5 py-3 text-xs text-[#69747C] border-t border-[#EDE7D6]">
                  {page.screenshot.caption}
                </figcaption>
              ) : null}
            </figure>
          ) : null}

          {page.introList?.length ? (
            <ul className="mt-4 space-y-2">
              {page.introList.map((item, i) => (
                <li key={i} className="flex items-start gap-2.5 text-base text-[#1F2326]">
                  <Check className="w-4 h-4 mt-1 text-[#B89B5E] flex-none" strokeWidth={2.5} />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          ) : null}

          {page.sections?.map((s, i) => <Section key={i} section={s} />)}

          {page.faqs?.length ? (
            <section className="mt-10 space-y-5" data-testid="page-faqs">
              {page.faqs.map((f, i) => (
                <div key={i} className="bg-white border border-[#E4DFD2] rounded-xl p-5">
                  <div className="font-display text-lg font-semibold text-[#1F2326]">{f.q}</div>
                  <p className="text-sm text-[#1F2326] mt-2 leading-relaxed">{f.a}</p>
                </div>
              ))}
            </section>
          ) : null}

          {page.cta ? <ConversionBlock onCta={handleCta} ctaTestId="marketing-cta" /> : null}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default MarketingPage;
